import { useState } from "react";
import { toast } from "sonner";

import apiClient, { getApiErrorMessage } from "@/lib/api";

const MAX_CV_SIZE_BYTES = 5 * 1024 * 1024;

const isPdfContentType = (contentType) => {
  if (!contentType) return true;
  return ["application/pdf", "application/x-pdf", "binary/octet-stream"].includes(contentType);
};

const EMPTY_APPLICATION_DATA = {
  name: "",
  email: "",
  phone: "",
};

export const useApplicationForm = () => {
  const [applicationData, setApplicationData] = useState(EMPTY_APPLICATION_DATA);
  const [cvFile, setCvFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleApplicationChange = (event) => {
    const { name, value } = event.target;
    setApplicationData((previous) => ({ ...previous, [name]: value }));
  };

  const clearCvFile = (fileInputId) => {
    setCvFile(null);
    if (fileInputId) {
      const fileInput = document.getElementById(fileInputId);
      if (fileInput) {
        fileInput.value = "";
      }
    }
  };

  const resetApplicationForm = (fileInputId) => {
    setApplicationData(EMPTY_APPLICATION_DATA);
    clearCvFile(fileInputId);
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Seuls les fichiers PDF sont acceptés.");
      event.target.value = "";
      return;
    }

    if (!isPdfContentType(file.type)) {
      toast.error("Le fichier doit être au format PDF.");
      event.target.value = "";
      return;
    }

    if (file.size > MAX_CV_SIZE_BYTES) {
      toast.error("Le fichier ne doit pas dépasser 5 Mo.");
      event.target.value = "";
      return;
    }

    setCvFile(file);
  };

  const submitApplication = async ({ selectedJob = null, onSuccess } = {}) => {
    if (!cvFile) {
      toast.error("Veuillez télécharger votre CV (format PDF).");
      return false;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("name", applicationData.name);
      formData.append("email", applicationData.email);
      formData.append("phone", applicationData.phone || "");
      formData.append("cv", cvFile);

      if (selectedJob) {
        formData.append("job_offer_id", selectedJob.id);
        formData.append("job_offer_title", selectedJob.title);
      }

      const response = await apiClient.post("/apply", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data?.success) {
        toast.success(response.data.message, { duration: 5000, icon: "🎉" });
        if (onSuccess) {
          onSuccess(response.data);
        }
        return true;
      }

      return false;
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Une erreur est survenue."), {
        duration: 5000,
        icon: "❌",
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    applicationData,
    cvFile,
    isSubmitting,
    handleApplicationChange,
    handleFileChange,
    clearCvFile,
    resetApplicationForm,
    submitApplication,
  };
};
